import type { BrickId, Mm } from '@bw/core';

/** A material a brick references by id (design document D1). */
export interface Material {
  readonly id: string;
  readonly name: string;
  /** Six-digit hex, upper case. The only field phase 1 reads. */
  readonly colour: string;
  /** Reserved for the phase-2 texture set. */
  readonly texture: string | null;
  readonly roughness: number | null;
}

/** Reserved for phase 2 — validated and round-tripped, never created in M1. */
export interface Opening {
  readonly id: string;
  readonly type: 'window' | 'door' | 'other';
  readonly x: Mm;
  readonly y: Mm;
  readonly width: Mm;
  readonly height: Mm;
  readonly revealMm: Mm;
}

/** Where a wall sits in plan. Defaulted and unused by the 2D editor (F1). */
export interface WallPlacement {
  readonly origin: { readonly x: Mm; readonly y: Mm; readonly z: Mm };
  readonly rotationDeg: number;
  readonly thicknessMm: Mm;
}

/**
 * A wall's design length along its own x axis.
 *
 * Normally a wall is as long as the bricks in it, and that is still the
 * default. But a bonded corner has courses that run PAST the point where the
 * centrelines meet — that is what fills the outside of the corner — and if the
 * outline were read from the brickwork those courses would drag it with them
 * and the footprint would stop closing. So a wall that has been set out from
 * its corners records the design line here, and the plan uses it instead.
 */
export interface PlanSpan {
  readonly minX: Mm;
  readonly maxX: Mm;
}

export interface Envelope {
  readonly x: Mm;
  readonly y: Mm;
  readonly w: Mm;
  readonly h: Mm;
}

export interface ProjectMeta {
  readonly name: string;
  readonly created: string;
  readonly modified: string;
  readonly app: string;
}

export interface ViewState {
  readonly wallId: string;
  readonly scale: number;
  readonly originX: number;
  readonly originY: number;
}

/**
 * The roof over the whole building (phase 2, M7).
 *
 * One roof per project: single storey, one outline, one roof. It is derived
 * from the footprint rather than drawn, so these four numbers are the entire
 * specification.
 */
export interface RoofSpec {
  readonly style: 'hip' | 'gable' | 'none';
  readonly pitchDeg: number;
  readonly overhangMm: Mm;
  readonly materialId: string;
}

/**
 * Defaults for every generated window and door (phase 2, M7).
 *
 * Sketch shapes are the source of truth for openings, and a flat ring in the
 * elevation knows nothing about depth — so the reveal comes from here. Fifty
 * millimetres is a little under half a 102.5 mm leaf, which is what a real
 * reveal tends to be.
 */
export interface JoinerySpec {
  readonly revealMm: Mm;
  readonly frameMm: Mm;
  readonly glassInsetMm: Mm;
  readonly frameColour: string;
}

export const DEFAULT_ROOF: RoofSpec = {
  style: 'hip',
  pitchDeg: 35,
  overhangMm: 300,
  // Charcoal, from the standard palette — tiles come from the same list of
  // materials as bricks rather than a colour of their own, so the tile picker
  // has something real to select.
  materialId: 'm6',
};

export const DEFAULT_JOINERY: JoinerySpec = {
  revealMm: 50,
  frameMm: 60,
  glassInsetMm: 25,
  frameColour: '#F2EFE8',
};

export const DEFAULT_PLACEMENT: WallPlacement = {
  origin: { x: 0, y: 0, z: 0 },
  rotationDeg: 0,
  thicknessMm: 102.5,
};

export type UnknownKeys = Readonly<Record<string, unknown>>;

/** Brick-level keys a future version wrote that this build does not know. */
export type BrickExtras = ReadonlyMap<BrickId, UnknownKeys>;
